// New version src/Common/controls/textfield/inputmasks_aliases_service.ts
// Old version src/CaseDotStar.ServicePackages.Frontend.Common/scripts/common/input_mask_aliases_run.js

import { Injectable } from '@angular/core';
import {
	Observable,
	Subscription,
} from 'rxjs';
import {
	clone,
	get,
	merge,
} from 'lodash';
import * as Inputmask from 'inputmask';

import { commonConstants } from '../../constants/common.constants';
import { CommonLocaleService } from '../../services/locale/locale.service';
import { CommonServerDateService } from '../../services/server_date.service';
import { CommonGeneralSettingsService } from '../../services/general_settings.service';

declare interface IMask {
	mask: string;
	placeholder?: string;
	showMaskOnHover?: boolean;
	showMaskOnFocus?: boolean;
	hourFormat?: string;
	alias?: string;
	leapday?: string;
	separator?: string;
	yearrange?: {
		minyear: number;
		maxyear: number;
	};
}

const { defaultNumberDecimalSeparator, defaultNumberGroupSeparator, defaultNumberDigitsAfterDecimal } = commonConstants;

@Injectable({
	providedIn: 'root',
})
export class CommonTextfieldInputMaskAliasesService {
	private saveCharsRegExp = new RegExp('[^\\\\:/*?"<>|]');
	private tagCharsRegExp = new RegExp('^[0-9A-Za-z_]');
	private externalTagCharsRegExp = new RegExp('^[0-9A-Za-z_\\[\\]]');
	private domainRegExp = new RegExp('^[0-9a-zA-Z-]');
	private scriptNameRegExp = new RegExp('^[0-9a-zA-Z-_]');
	private onlyLettersRegExp = new RegExp(/(^$)|(^([^\-!#$%&()*,./:;?@\[\\\]_{|}¨ˇ“”€+<=>§°\d\s¤®™©'"^±~`№])+$)/); // all letters
	private displayNameRegExp = new RegExp(/(^$)|(^([^\-!#$%&()*,./:;?@\[\\\]_{|}¨ˇ“”€+<=>§°\d\s¤®™©№^±~'"]| )+$)/); // all letters, quotes and gaps
	private numberFormat = this.commonGeneralSettingsService.getGeneralSettings('NumberFormat');
	private decimalSeparator = get(this.numberFormat, 'DecimalSeparator') || defaultNumberDecimalSeparator;
	private groupSeparator = get(this.numberFormat, 'GroupSeparator') || defaultNumberGroupSeparator;

	private numericParams = {
		alias: 'numeric',
		radixPoint: this.decimalSeparator,
		groupSeparator: this.groupSeparator,
		autoGroup: true,
		rightAlign: false,
		unmaskAsNumber: true,
		digits: defaultNumberDigitsAfterDecimal.toString(),
		allowMinus: true,
	};

	private integerParams = clone(this.numericParams);
	private integerPositiveParams = clone(this.integerParams);
	private numericParamsNoMinus = clone(this.numericParams);

	constructor (
		private commonGeneralSettingsService: CommonGeneralSettingsService,
		private commonServerDateService: CommonServerDateService,
		private commonLocaleService: CommonLocaleService,
	) {}

	init (): Subscription {
		return new Observable(() => {
			this.numericParamsNoMinus.allowMinus = false;
			this.integerParams.alias = 'integer';
			this.integerParams.digits = '0';
			this.integerPositiveParams.allowMinus = false;

			Inputmask
				.extendAliases({
					// text mask with only digits ("00001")
					'casem-text-digits': {
						mask: 'b',
						repeat: '*',
						greedy: false,
						autoUnmask: true,
						showMaskOnHover: false,
						showMaskOnFocus: false,
						isComplete: (buffer: string[]): boolean => commonConstants.dataValidation.digits.test(buffer.join('')),
						definitions: {
							b: {
								validator: (chr: string): boolean => commonConstants.dataValidation.digits.test(chr),
							},
						},
					},
					'casem-phone': {
						mask: '\\+9{*}',
						showMaskOnHover: false,
						showMaskOnFocus: true,
					},
					'casem-phone-numb-11': {
						mask: '\\+7 999 999-99-99',
						showMaskOnHover: false,
						showMaskOnFocus: true,
						autoUnmask: true,
						onBeforeMask: (value: string): string => {
							const newValue = value.replace(/[+\s_-]/g, '');
							// это необходимо если номер телефона уже существует, потому что inputmask добавляет 7 вначало строки и номер телефона обрезается
							// 7 999 999 99 99 => 7 799 999 99 99
							if (newValue.length === 11) {
								return newValue.slice(1);
							}
							return newValue;
						},
						onUnMask: (maskedValue: string, unmaskedValue: string): string => !unmaskedValue ? unmaskedValue : maskedValue.replace(/[+\s_-]/g, ''),
					},
					'casem-safe-characters': {
						mask: 'c',
						greedy: false,
						repeat: '*', // http://git.io/vcguC - transmits value to model
						isComplete: (buffer: string[]): boolean =>
							// Logic broadcast 'isComplete' for model
							 this.saveCharsRegExp.test(buffer.join(''))
						,
						definitions: {
							c: {
								validator: (chr: string): boolean => this.saveCharsRegExp.test(chr),
							},
						},
					},
					'casem-tag': {
						mask: 'd',
						greedy: false,
						repeat: '*', // http://git.io/vcguC - transmits value to model
						isComplete: (buffer: string[]): boolean =>
							// Logic broadcast 'isComplete' for model
							 this.tagCharsRegExp.test(buffer.join(''))
						,
						definitions: {
							d: {
								validator: (chr: string): boolean => this.tagCharsRegExp.test(chr),
							},
						},
					},
					'casem-external-tag': {
						mask: 'e',
						greedy: false,
						repeat: '+', // http://git.io/vcguC - transmits value to model
						isComplete: (buffer: string[]): boolean =>
							// Logic broadcast 'isComplete' for model
							 this.externalTagCharsRegExp.test(buffer.join(''))
						,
						definitions: {
							e: {
								validator: (chr: string): boolean => this.externalTagCharsRegExp.test(chr),
							},
						},
					},
					'casem-domain': {
						mask: 'f',
						greedy: false,
						repeat: '*', // http://git.io/vcguC - transmits value to model
						isComplete: (buffer: string[]) =>
							// Logic broadcast 'isComplete' for model
							 this.domainRegExp.test(buffer.join(''))
						,
						definitions: {
							f: {
								validator: (chr: string): boolean => this.domainRegExp.test(chr),
							},
						},
					},
					'casem-script-name': {
						mask: '*',
						greedy: false,
						repeat: '*', // http://git.io/vcguC - transmits value to model
						isComplete: (buffer: string[]) =>
							// Logic broadcast 'isComplete' for model
							 this.scriptNameRegExp.test(buffer.join(''))
						,
						definitions: {
							'*': {
								validator: (chr: string): boolean => this.scriptNameRegExp.test(chr),
							},
						},
					},
					'casem-numeric': this.numericParams,
					'casem-numeric-no-minus': this.numericParamsNoMinus,
					'casem-integer': this.integerParams,
					'casem-integer-positive': this.integerPositiveParams,
					'casem-initials': {
						mask: 'g',
						greedy: false,
						repeat: '3', // http://git.io/vcguC - transmits value to model
						isComplete: (buffer: string[]) =>
							// Logic broadcast 'isComplete' for model
							 this.onlyLettersRegExp.test(buffer.join(''))
						,
						definitions: {
							g: {
								validator: (chr: string): boolean => this.onlyLettersRegExp.test(chr),
								casing: 'upper',
							},
						},
					},
					'casem-letters': {
						mask: '*',
						greedy: false,
						repeat: '+',
						isComplete: (buffer: string[]) => this.onlyLettersRegExp.test(buffer.join('')),
						definitions: {
							'*': {
								validator: (chr: string): boolean => this.onlyLettersRegExp.test(chr),
							},
						},
					},
					'casem-displayname': {
						mask: 'h',
						greedy: false,
						repeat: '+', // http://git.io/vcguC - transmits value to model
						isComplete: (buffer: string[]): boolean =>
							// Logic broadcast 'isComplete' for model
							 this.displayNameRegExp.test(buffer.join(''))
						,
						definitions: {
							h: {
								validator: (chr: string): boolean => this.displayNameRegExp.test(chr),
							},
						},
					},
					'casem-cardnumber': {
						mask: 'i',
						repeat: '24',
						greedy: false,
						autoUnmask: true,
						showMaskOnHover: false,
						showMaskOnFocus: false,
						isComplete: (buffer: string[]): boolean => commonConstants.dataValidation.cardNumber.test(buffer.join('')),
						definitions: {
							i: {
								validator: (chr: string): boolean => commonConstants.dataValidation.cardNumber.test(chr),
							},
						},
					},
					'casem-invoice-time-rounding-minutes': {
						alias: 'numeric',
						repeat: '3',
						greedy: false,
						rightAlign: false,
						unmaskAsNumber: true,
						allowMinus: false,
						digits: '0',
					},
					'casem-invoice-time-rounding-hours': {
						alias: 'numeric',
						repeat: '2',
						greedy: false,
						rightAlign: false,
						unmaskAsNumber: true,
						allowMinus: false,
						digits: '2',
					},
					'casem-time': this.getCasemTimeMask(),
					'casem-date': this.getCasemDateMask(),
					'casem-cardexpire': this.getCasemCardExpireMask(),
					'casem-cardexpire-month': this.getCasemCardExpireMonthMask(),
					'casem-cardexpire-year': this.getCasemCardExpireYearhMask(),
					'casem-participant-individual-inn': this.getCasemParticipantIndividualInnMask(),
					'casem-participant-inn-organization': {
						mask: '9999999999',
						autoUnmask: true,
						greedy: false,
						showMaskOnHover: false,
					},
					'casem-participant-inn-person': {
						mask: '999999999999',
						autoUnmask: true,
						greedy: false,
						showMaskOnHover: false,
					},
					'meetings-individual-ogrn': {
						mask: '9999999999999[99]',
						autoUnmask: true,
						greedy: false,
					},
					'meetings-passport': {
						mask: '99 99 999999',
						showMaskOnHover: false,
						showMaskOnFocus: true,
						autoUnmask: true,
					},
					'meetings-subdivision-code': {
						mask: '999-999',
						showMaskOnHover: false,
						showMaskOnFocus: true,
						autoUnmask: true,
					},
				});
		})
			.subscribe();
	}

	private getCasemDateMask (): IMask {
		const inputDateMask = this.commonServerDateService.inputDateMask;
		const mask = inputDateMask.replace(/dd|mm/, '1').replace(/dd|mm/, '2').replace('yyyy', 'y');
		const alias = inputDateMask.replace(/\W/g, '/');
		const leapday = inputDateMask.replace('dd', '29').replace('mm', '02').replace('yyyy', '');
		const separator = inputDateMask.match(/\W/)![0];

		return {
			mask,
			placeholder: this.commonServerDateService.inputDatePlaceholder,
			leapday,
			separator,
			alias,
			yearrange: {
				minyear: 1900,
				maxyear: 2999,
			},
			showMaskOnHover: false,
			showMaskOnFocus: true,
		};
	}

	private getCasemTimeMask (): IMask {
		return {
			mask: this.commonServerDateService.inputTimeMask,
			placeholder: this.commonServerDateService.inputTimePlaceholder,
			alias: 'datetime',
			hourFormat: this.commonGeneralSettingsService.hasMeridiem() ? '12' : '24',
			showMaskOnHover: false,
			showMaskOnFocus: true,
		};
	}

	private getCasemCardExpireMask (): IMask {
		const monthsSymbol = this.commonLocaleService.instant('common.symbols.months');
		const yearsSymbol = this.commonLocaleService.instant('common.symbols.years');

		return {
			mask: '99/99',
			placeholder: (monthsSymbol + monthsSymbol + '/' + yearsSymbol + yearsSymbol).toUpperCase(),
			showMaskOnHover: false,
			showMaskOnFocus: false,
		};
	}

	private getCasemCardExpireMonthMask (): IMask {
		const monthsSymbol = this.commonLocaleService.instant('common.symbols.months');

		return {
			mask: '99',
			placeholder: (monthsSymbol + monthsSymbol).toUpperCase(),
			showMaskOnHover: false,
			showMaskOnFocus: false,
		};
	}

	private getCasemCardExpireYearhMask (): IMask {
		const yearsSymbol = this.commonLocaleService.instant('common.symbols.years');

		return {
			mask: '(99)|(9999)',
			placeholder: (yearsSymbol + yearsSymbol).toUpperCase(),
			showMaskOnHover: false,
			showMaskOnFocus: false,
		};
	}

	private getCasemParticipantIndividualInnMask (): IMask {
		const locale = this.commonLocaleService.locale;

		const masks = {
			ru: {
				mask: '9999999999[99]',
			},
		};

		const maskParams = {
			autoUnmask: true,
			greedy: false,
		};

		return merge(masks[locale], maskParams);
	}
}
